import vuexStore from "@/store";

/**
 * This is a helper class designated for managing the autosave feature as well as an optional manual save.
 *
 * Important! When using it with a Vue component, be sure to assign the instance to a `reactive` variable, such as a property declared in the `data` method. Otherwise you may face reactivity issues.
 *
 * @property {Boolean} isSaving - Stores whether or not the onSave callback is running
 * @property {Boolean} pendingSave - Stores whether or not there are changes that need to be saved, whether they actually get to run or not.
 * @property {Boolean} isError - Stores whether or not the onSave function has thrown an error
 */
export class AutoSaveManager {
  /**
   * @constructor
   * @param {Object} params
   *   @property {String} formName - The name of the form. Used to display to the user which form is pending submission
   *   @property {Function} beforeSave - The function that will be called before saving, in order to validate if it can be saved. The return value must be a boolean indicating whether or not to proceed with saving. Can return a promise.
   *   @property {Function} onSave - The function that will be called to save the form. Can be a promise.
   *   @property {Number} autoSaveTimeout - Optional value to control the autosave debounce time.
   *   @property {Boolean} showUnsavedAlertOnLeave - Optional value to decide whether or not show an alert to the user in case there are any unsaved changes.
   *   @property {Object} store - Dependency Injection for VueX store
   */
  constructor({
    formName,
    beforeSave = () => true,
    onSave,
    autoSaveTimeout = 2000,
    showUnsavedAlertOnLeave = true,
    store = vuexStore,
  }) {
    this.formName = formName;
    this.beforeSave = beforeSave;
    this.onSave = onSave;
    this.autoSaveTimeout = autoSaveTimeout;
    this.showUnsavedAlertOnLeave = showUnsavedAlertOnLeave;
    this.store = store;

    this._validateConstructorParams(["formName", "onSave"]);

    this.saveTimeout = null;
    this.lastSaveDispatchTime = 0;

    this.isSaving = false;
    this.pendingSave = false;
    this.isError = false;
  }

  /**
   * Dispatches the save to be run in `this.autoSaveTimeout` milliseconds.
   *
   * Will not actually run the auto save if a manual save has been pressed after the dispatch.
   */
  dispatchAutoSave() {
    clearTimeout(this.saveTimeout);

    const saveDispatchTime = new Date();

    this._markPendingSave();

    this.saveTimeout = setTimeout(() => {
      this._validateAndTriggerSave({ saveDispatchTime });
    }, this.autoSaveTimeout);
  }

  /**
   * Triggers the save immediately.
   */
  async triggerManualSave() {
    this._markPendingSave();

    await this._validateAndTriggerSave({
      saveDispatchTime: new Date(),
    });
  }

  async _validateAndTriggerSave({ saveDispatchTime }) {
    if (saveDispatchTime < this.lastSaveDispatchTime) {
      return;
    }

    this.lastSaveDispatchTime = saveDispatchTime;

    const shouldSave = await this.beforeSave();
    if (!shouldSave) return;

    this.isSaving = true;

    await this._triggerSave();

    this._clearPendingSave();
    this.isSaving = false;
  }

  async _triggerSave() {
    try {
      this.isError = false;
      await this.onSave();
    } catch (e) {
      this.isError = true;
      throw e;
    }
  }

  _markPendingSave() {
    this.pendingSave = true;

    if (this.showUnsavedAlertOnLeave) {
      this.store.commit("markFormPendingSubmission", this.formName);
    }
  }

  _clearPendingSave() {
    this.pendingSave = false;
    this.store.commit("clearFormPendingSubmission", this.formName);
  }

  _validateConstructorParams(params) {
    params.forEach((param) => {
      if (!this[param]) {
        throw new Error(`[AutoSaveManager] "${param}" argument is required`);
      }
    });
  }
}
